import SVG from "@/assets/svg";
import { TCheckInCheckOut } from "@/component/drawer/DetailTab";
import { Visit } from "@/interfaces/IServiceUser";
import { useAppSelector } from "@/redux/hooks";
import { $capitalize, getCheckoutReasonType, getId } from "@/utils/helpers";
import type { ChipProps } from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import React, { useMemo } from "react";

type CheckoutReasonProps = {
  onClose: () => void;
  selectedLog?:
    | (TCheckInCheckOut & {
        type: "checkIn" | "checkOut";
      })
    | null;
  setting: any;
};

export default function CheckoutReason({
  selectedLog,
  onClose,
  setting,
}: CheckoutReasonProps) {
  const { selectedVisit } = useAppSelector((state) => state.roster);
  const visit = selectedVisit?.extendedProps as Visit;

  const checkoutReasonType = useMemo(() => {
    if (!setting || !visit || !selectedLog?.checkOut) {
      return null;
    }

    return getCheckoutReasonType({
      setting,
      visit,
      checkOutTime: dayjs(selectedLog.checkOut),
      carerId: getId(selectedLog?.carer),
    });
  }, [setting, visit, selectedLog]);

  const getCheckoutUI = (
    type: "late" | "early" | string
  ): {
    color: ChipProps["color"];
    label: string;
  } => {
    switch (type) {
      case "late":
        return {
          color: "error",
          label: "Overrun Reason",
        };

      case "early":
        return {
          color: "warning",
          label: "Underrun Reason",
        };

      default:
        return {
          color: "default",
          label: "Checkout Reason",
        };
    }
  };

  const ui = useMemo(
    () => getCheckoutUI(checkoutReasonType),
    [checkoutReasonType]
  );

  return (
    <Box className="checkoutReason">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          color={ui.color}
          sx={{
            fontWeight: 600,
            color: selectedLog?.auto ? "#4682d5" : undefined,
          }}
        >
          {selectedLog?.auto ? "Auto Checkout Reason" : `${ui.label}`}
        </Typography>

        <Box
          onClick={onClose}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <SVG.Cross />
        </Box>
      </Box>

      {selectedLog?.lateOrEarlyCheckoutReason && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mb: 0.5,
            }}
          >
            {`${$capitalize(checkoutReasonType || "")} Checkout Reason`}
          </Typography>
          <Box
            sx={{
              p: 1.5,
              bgcolor: "grey.100",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              {selectedLog.lateOrEarlyCheckoutReason}
            </Typography>
          </Box>
        </Box>
      )}

      {selectedLog?.lateOrEarlyCheckoutReasonNote && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mb: 0.5,
            }}
          >
            Additional Notes
          </Typography>

          <Box
            sx={{
              p: 1.5,
              bgcolor: "grey.100",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              {selectedLog.lateOrEarlyCheckoutReasonNote}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
